// /reports/top-products — Top Products (PAGES.md §3.45)

import { useState, useMemo } from 'react';
import { Select, BarChart } from '@bbpos/ui';
import type { ColumnDef, TimeRangeValue } from '@bbpos/ui';
import { formatPaise } from '@/utils/currency';
import { getSubunitMultiplier } from '@bbpos/utils';
import type { CurrencyCode } from '@bbpos/types';
import {
  ReportPageShell,
  ReportFilterBar,
  ReportTable,
  ReportExportMenu,
  ReportSkeleton,
  ReportError,
  useReportFilter,
  useReportQuery,
  reportsApi,
  downloadCsv,
  shareReportLink,
  DEFAULT_REPORT_PRESET,
} from '@/features/reports';
import type { TopProductRow } from '@/features/reports';
import { useTenantCurrency, getCurrencySymbol } from '@/hooks/use-tenant-currency';

type Row = TopProductRow & { id: string };

function makeColumns(currency: CurrencyCode): ColumnDef<Row>[] {
  return [
    { key: 'rank', header: '#', render: (_v, row) => <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-brand-500 to-indigo-500 text-xs font-bold text-white tabular-nums">{row.rank}</span> },
    {
      key: 'product_name',
      header: 'Product',
      render: (_v, row) => (
        <div>
          <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">{row.product_name}</p>
          {row.category ? <p className="text-xs text-neutral-500 dark:text-neutral-400">{row.category}</p> : null}
        </div>
      ),
    },
    { key: 'quantity_sold', header: 'Qty Sold', numeric: true, render: (_v, row) => <span className="tabular-nums">{row.quantity_sold.toLocaleString('en-IN')}</span> },
    { key: 'revenue', header: 'Revenue', numeric: true, render: (_v, row) => <span className="text-sm font-semibold tabular-nums">{formatPaise(row.revenue, currency)}</span> },
  ];
}

export function TopProductsReportPage() {
  const [timeRange, setTimeRange] = useState<TimeRangeValue>(DEFAULT_REPORT_PRESET);
  const [locationId, setLocationId] = useState<string | undefined>(undefined);
  const [limit, setLimit] = useState<10 | 25 | 50>(10);
  const [sort, setSort] = useState<'revenue' | 'quantity'>('revenue');
  const filter = useReportFilter(timeRange, locationId);
  const currency = useTenantCurrency();
  const symbol = getCurrencySymbol(currency);
  const subunit = getSubunitMultiplier(currency);
  const csvDecimals = Math.round(Math.log10(subunit));
  const columns = useMemo(() => makeColumns(currency), [currency]);

  const { data, isLoading, error } = useReportQuery({
    key: 'top-products',
    filter,
    extraKey: [limit, sort],
    queryFn: (f) => reportsApi.getTopProducts(f, limit, sort),
  });

  const rows: Row[] = useMemo(
    () => (data?.items ?? []).map((p) => ({ ...p, id: p.product_id })),
    [data],
  );

  const chartData = rows.slice(0, 10).map((r) => ({
    label: r.product_name.length > 14 ? `${r.product_name.slice(0, 14)}…` : r.product_name,
    value: sort === 'revenue' ? r.revenue / subunit : r.quantity_sold,
  }));

  const handleExport = (fmt: 'csv' | 'pdf' | 'whatsapp' | 'email') => {
    if (fmt === 'pdf') {
      // Browser print dialog -> "Save as PDF"; no server-side PDF renderer exists.
      window.print();
      return;
    }
    if (fmt === 'csv') {
      downloadCsv(
        `top-products-${filter.range.from}-to-${filter.range.to}.csv`,
        [
          { header: 'Rank', get: (r: Row) => r.rank },
          { header: 'Product', get: (r: Row) => r.product_name },
          { header: 'Category', get: (r: Row) => r.category ?? '' },
          { header: 'Qty Sold', get: (r: Row) => r.quantity_sold },
          { header: `Revenue (${symbol})`, get: (r: Row) => (r.revenue / subunit).toFixed(csvDecimals) },
        ],
        rows,
      );
      return;
    }
    if (fmt === 'whatsapp' || fmt === 'email') {
      shareReportLink(fmt, 'Orio360 Top Products', `Top seller: ${rows[0]?.product_name ?? '—'} (${formatPaise(rows[0]?.revenue ?? 0, currency)})`);
    }
  };

  return (
    <ReportPageShell
      title="Top Products"
      subtitle="Ranked best-sellers by revenue or quantity."
      breadcrumbs={[{ label: 'Home', href: '/home' }, { label: 'Reports', href: '/reports' }, { label: 'Top Products' }]}
      accent="brand-indigo"
      headerActions={<ReportExportMenu onExport={handleExport} disabled={!rows.length} />}
      filterBar={
        <ReportFilterBar
          timeRange={timeRange}
          onTimeRangeChange={setTimeRange}
          locationId={locationId}
          onLocationChange={setLocationId}
          extraFilters={
            <>
              <div className="w-full sm:w-32">
                <Select
                  options={[
                    { value: '10', label: 'Top 10' },
                    { value: '25', label: 'Top 25' },
                    { value: '50', label: 'Top 50' },
                  ]}
                  value={String(limit)}
                  onChange={(v) => setLimit(Number(v) as 10 | 25 | 50)}
                />
              </div>
              <div className="w-full sm:w-40">
                <Select
                  options={[
                    { value: 'revenue', label: 'By Revenue' },
                    { value: 'quantity', label: 'By Quantity' },
                  ]}
                  value={sort}
                  onChange={(v) => setSort(v as 'revenue' | 'quantity')}
                />
              </div>
            </>
          }
        />
      }
    >
      {isLoading ? (
        <ReportSkeleton kpiCount={0} />
      ) : error ? (
        <ReportError />
      ) : (
        <>
          {chartData.length > 0 ? (
            <div className="rounded-2xl bg-white dark:bg-neutral-800/80 dark:backdrop-blur-xl shadow-md p-5 mb-6 animate-slide-up">
              <BarChart data={chartData} title={sort === 'revenue' ? `Top Products by Revenue (${symbol})` : 'Top Products by Units Sold'} />
            </div>
          ) : null}
          <ReportTable title={`Top ${limit}`} columns={columns} data={rows} emptyTitle="No sales data for selected period" totalCount={rows.length} />
        </>
      )}
    </ReportPageShell>
  );
}
